<template>
  <div id="top-bar-component">
    <v-app-bar
      window
      class="first-top-bar px-xs-2 px-sm-5 px-md-10"
      :extension-height="toolbarExtensionHeight"
    >
      <v-btn
        v-if="$vuetify.display.smAndDown && pod"
        @click="toggleSidemenu()"
        class="mx-1"
        height="48px"
        width="48px"
        :elevation="0"
        size="large"
        icon="mdi-view-split-horizontal"
        :aria-label="$t('routes.accesslink.mediaInformation')"
      />

      <a v-if="!getBranginLogoUrl" class="main-logo mx-1">
        <img
          v-if="$vuetify.theme.name === 'light'"
          src="../../../assets/images/tw-main-logo-light.png"
          :alt="getBrandingInfo?.name"
        />
        <img
          v-if="$vuetify.theme.name === 'dark'"
          src="../../../assets/images/tw-main-logo-dark.png"
          :alt="getBrandingInfo?.name"
        />
      </a>
      <a v-else
         :href="getBrandingHomepage"
         class="main-logo mx-1 d-flex align-center justify-center"
         style="width: auto !important;"
      >
        <img :src="getBranginLogoUrl" style="width: auto !important;" :alt="getBrandingInfo?.name"/>
      </a>

      <v-tooltip
        v-if="(pod || node) && (getAccesslinkPods.length > 1 || getAccesslinkNodes.length > 1 || (getAccesslinkNodes.length === 1 && getAccesslinkNodes[0].pods.length > 1))"
        location="bottom"
        :aria-label="$t('routes.accesslink.backToFileList')"
      >
        <template v-slot:activator="{ props }">
          <v-btn
            v-if="$vuetify.display.mdAndUp"
            v-bind="props"
            class="mx-3"
            @click="navigateToParentDirectory"
            variant="text"
            prepend-icon="mdi-keyboard-backspace"
            :aria-label="$t('routes.accesslink.backToFileList')"
          >
            {{ $t('common.back') }}
          </v-btn>
          <v-btn
            v-if="$vuetify.display.smAndDown"
            v-bind="props"
            class="mx-3"
            @click="navigateToParentDirectory"
            variant="text"
            icon
            :aria-label="$t('routes.accesslink.backToFileList')"
          >
            <v-icon>mdi-keyboard-backspace</v-icon>
          </v-btn>
        </template>

        <span>{{ $t("routes.accesslink.backToFileList") }}</span>
      </v-tooltip>

      <v-spacer/>

      <div v-if="!$vuetify.display.smAndDown" class="entity-title">
        {{ getEntityName }}
      </div>

      <v-tooltip
        v-if="pod && quickSwitcherPods.length > 1"
        open-delay="250"
        z-index="100"
        location="bottom"
        :aria-label="$t('routes.pod.topBar.quickFileSwitch')"
      >
        <template v-slot:activator="{ props }">
          <v-btn
            v-bind="props"
            :elevation="0"
            :size="$vuetify.display.smAndDown ? 'small' : undefined"
            :aria-label="$t('routes.pod.topBar.quickFileSwitch')"
            class="mx-1"
            variant="tonal"
            min-width="32"
            width="50"
            rounded="circle"
            @click="toggleQuickSwitcher"
            style="border-radius: 50%;"
          >
            <v-icon size="medium">mdi-view-carousel-outline</v-icon>
          </v-btn>
        </template>
        <span>{{ $t('routes.pod.topBar.quickFileSwitch') }}</span>
      </v-tooltip>

      <v-spacer v-if="$vuetify.display.xs" />

      <div v-if="showVersions" class="mx-2">
        <v-menu>
          <template v-slot:activator="{ props }">
            <v-btn
              :elevation="0"
              size="small"
              v-bind="props"
              prepend-icon="mdi-chevron-down"
              :aria-label="$t('routes.accesslink.versions')"
            >
              {{ $t("routes.accesslink.versions") }}
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              v-for="(item, index) in getVersions"
              :key="index"
              @click="handleVersionSelect(item)"
              lines="two"
            >
              <v-list-item-title>{{ item.name }}</v-list-item-title>
              <v-list-item-subtitle>{{ item.versiontext }}</v-list-item-subtitle>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>

      <v-spacer v-if="!$vuetify.display.xs" />

      <div v-if="!$vuetify.display.xs && getExpireAt">
        {{ $t("routes.accesslink.linkExpiresAt") }} {{ isoToDate(getExpireAt) }}
        ({{ $t('common.time.days', getExpiryDays) }})
      </div>

      <template v-if="$vuetify.display.smAndDown" #extension>
        <div ref="mobileFileName" class="w-100 d-flex justify-center px-2" style="word-break: break-all;">
          {{ getEntityName }}
        </div>
      </template>
    </v-app-bar>
  </div>
</template>

<script>
import { useAccesslinkStore } from '@/stores/AccesslinkStore';
import { isoToDate } from '@/components/formatters.js';
import { throttle } from 'lodash';

export default {
  emits: ["toggleSidemenu"],
  name: "AccesslinkTopBar",

  beforeMount() {
    this.handleAppToolbarExtensionHeight = throttle(() => this.resizeAppToolbarExtension(), 100);
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.handleAppToolbarExtensionHeight);
  },

  mounted() {
    this.resizeAppToolbarExtension();
  },

  watch: {
    '$vuetify.display.smAndDown': function (val) {
      if (val) {
        this.$nextTick(() => {
          this.resizeAppToolbarExtension();
          window.addEventListener('resize', this.handleAppToolbarExtensionHeight);
        });
      } else {
        this.handleAppToolbarExtensionHeight.cancel();
        window.removeEventListener('resize', this.handleAppToolbarExtensionHeight);
      }
    }
  },

  methods: {
    isoToDate,
    toggleSidemenu() {
      this.$emit('toggleSidemenu');
    },
    toggleQuickSwitcher() {
      const accesslinkStore = useAccesslinkStore();
      accesslinkStore.toggleQuickSwitcher();
    },
    navigateToParentDirectory() {
      if (this.$route.params.nodeid && this.$route.params.podid) {
        this.$router.replace({
          name: 'accesslink:view:node',
          params: { nodeid: this.$route.params.nodeid }
        });
      } else {
        this.$router.replace({
          name: 'accesslink:view',
        });
      }
    },
    handleVersionSelect(version) {
      this.$router.push({
        name: this.$route.name,
        params: {
          ...this.$route.params,
          podid: version.id
        }
      });
    },
    resizeAppToolbarExtension() {
      if (this.$refs.mobileFileName) {
        const rect = this.$refs.mobileFileName.getBoundingClientRect();
        this.toolbarExtensionHeight = rect.height + 8;
      } else {
        this.toolbarExtensionHeight = 0
      }
    },
  },

  data: function () {
    return {
      toolbarExtensionHeight: 0,
    }
  },

  props: {
    pod: {
      type: Object,
      required: false,
    },
    parentPod: {
      type: Object,
      required: false,
    },
    node: {
      type: Object,
      required: false,
    }
  },

  computed: {
    getEntityName: function () {
      if (this.pod) return this.pod.name;
      else if (this.node) return this.node.name;
      return '';
    },

    getAccesslinkInfo: function () {
      const accesslinkStore = useAccesslinkStore();
      return accesslinkStore.getAccesslinkInfo;
    },
    getAccesslinkPods: function () {
      const accesslinkStore = useAccesslinkStore();
      return accesslinkStore.getAccesslinkPods;
    },
    getAccesslinkNodes: function () {
      const accesslinkStore = useAccesslinkStore();
      return accesslinkStore.getAccesslinkNodes;
    },
    getBrandingInfo: function () {
      if (this.getAccesslinkInfo && this.getAccesslinkInfo.brand) {
        return this.getAccesslinkInfo.brand;
      }

      return null;
    },
    getBrandingHomepage() {
      if (this.getBrandingInfo && this.getBrandingInfo.alias) {
        return this.getBrandingInfo.alias;
      }

      return null;
    },
    getBranginLogoUrl() {
      if (this.getBrandingInfo && this.getBrandingInfo.logoUrl) {
        return this.getBrandingInfo.logoUrl;
      }

      return null;
    },
    getExpireAt: function () {
      if (this.getAccesslinkInfo && this.getAccesslinkInfo.expireat) {
        return this.getAccesslinkInfo.expireat;
      }

      return "";
    },
    getExpiryDays: function () {
      if (this.getExpireAt) {
        const expDate = new Date(this.getExpireAt);
        const now = new Date();
        return Math.floor((expDate - now) / 86400000);
      }

      return '';
    },
    quickSwitcherPods() {
      const accesslinkStore = useAccesslinkStore();
      return accesslinkStore.quickSwitcherPods(this.$route.params.nodeid);
    },
    showVersions() {
      const addVersions = this.getAccesslinkInfo?.params?.addversions === 1;
      if (addVersions) {
        if (Array.isArray(this.parentPod?.childrenpods) && this.parentPod.childrenpods.length > 0) {
          return true;
        }
      }

      return false;
    },
    getVersions: function () {
      const versions = [];
      if (this.showVersions) {
        versions.push({
          id: this.parentPod.id,
          name: this.parentPod.name,
          versiontext: this.parentPod.versiontext,
        });

        for (const childPod of this.parentPod.childrenpods) {
          versions.push({
            id: childPod.id,
            name: childPod.name,
            versiontext: childPod.versiontext,
          });
        }
      }

      return versions;
    },
  }
};
</script>

<style lang="scss" scoped>
.entity-title {
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 1280px) {
  .entity-title {
    max-width: 40%;
  }
}

@media (max-width: 980px) {
  .entity-title {
    max-width: 35%;
  }
}
</style>
